import React, { useState, useEffect } from "react";
import { AREAS, getSchedulableDevices } from "../data/devices";
import "./css/schedules.css";

const ALL_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

const LIGHT_ACTIONS   = ["on", "off"];
const CURTAIN_ACTIONS = ["open", "close", "stop"];

function defaultState(schedule) {
  if (schedule) {
    return {
      name:       schedule.name || "",
      areaTitle:  schedule.zone ? (AREAS.find(a => getSchedulableDevices(a).some(d => {
        const cmd = a.commandMapping[d];
        return Object.keys(Object.values(cmd)[0] || {})[0] === schedule.zone;
      }))?.title || "") : "",
      deviceName: schedule.device_name,
      action:     schedule.action,
      time:       schedule.time,
      days:       Array.isArray(schedule.days) ? schedule.days : JSON.parse(schedule.days || "[]"),
      enabled:    schedule.enabled !== false,
    };
  }
  return {
    name: "", areaTitle: "", deviceName: "", action: "", time: "08:00",
    days: ["mon", "tue", "wed", "thu", "fri"], enabled: true,
  };
}

export default function ScheduleModal({ schedule, prefilledArea, onClose, onSave }) {
  const isEdit = !!schedule;

  const [form, setForm]       = useState(() => defaultState(schedule));
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  // When prefilledArea is provided (from DynamicPanel), lock the area
  useEffect(() => {
    if (prefilledArea && !isEdit) {
      setForm(f => ({ ...f, areaTitle: prefilledArea }));
    }
  }, [prefilledArea, isEdit]);

  // Derived selections
  const selectedArea    = AREAS.find(a => a.title === form.areaTitle) || null;
  const schedulableDevs = selectedArea ? getSchedulableDevices(selectedArea) : [];
  const areaType        = selectedArea?.type || 'light';
  const actions         = areaType === 'curtain' ? CURTAIN_ACTIONS : LIGHT_ACTIONS;

  function setField(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function handleAreaChange(title) {
    setForm(f => ({ ...f, areaTitle: title, deviceName: "", action: "" }));
  }

  function handleDeviceChange(name) {
    setForm(f => ({ ...f, deviceName: name, action: "" }));
  }

  function toggleDay(day) {
    setForm(f => {
      const exists = f.days.includes(day);
      return { ...f, days: exists ? f.days.filter(d => d !== day) : [...f.days, day] };
    });
  }

  function toggleAllDays() {
    setForm(f => ({
      ...f,
      days: f.days.length === 7 ? [] : [...ALL_DAYS],
    }));
  }

  function buildPayload() {
    if (!selectedArea || !form.deviceName || !form.action) return null;
    const cmd = selectedArea.commandMapping[form.deviceName];
    if (!cmd) return null;
    const command = cmd[form.action];
    if (!command || Array.isArray(command)) return null;
    const zone = Object.keys(command)[0];

    return {
      name:        form.name || null,
      device_type: areaType,
      zone,
      device_name: form.deviceName,
      action:      form.action,
      command,
      time:        form.time,
      days:        form.days,
      enabled:     form.enabled,
    };
  }

  async function handleSave() {
    setError("");
    if (!form.areaTitle)  return setError("Please select an area.");
    if (!form.deviceName) return setError("Please select a device.");
    if (!form.action)     return setError("Please select an action.");
    if (!form.time)       return setError("Please set a time.");
    if (form.days.length === 0) return setError("Select at least one day.");

    const payload = buildPayload();
    if (!payload) return setError("Could not resolve device command. Check device configuration.");

    setSaving(true);
    try {
      await onSave(payload, schedule?.id);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  }

  const isEveryDay = form.days.length === 7;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>{isEdit ? "Edit Schedule" : "New Schedule"}</h3>

        {error && <div className="schedules-error" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="modal-field">
          <label>Schedule Name (optional)</label>
          <input
            className="modal-input"
            type="text"
            placeholder="e.g. Morning lights"
            value={form.name}
            onChange={e => setField("name", e.target.value)}
          />
        </div>

        <div className="modal-field">
          <label>Area</label>
          <select
            className="modal-select"
            value={form.areaTitle}
            onChange={e => handleAreaChange(e.target.value)}
            disabled={!!prefilledArea && !isEdit}
          >
            <option value="">— Select area —</option>
            {AREAS.map(a => (
              <option key={a.title} value={a.title}>{a.title}</option>
            ))}
          </select>
        </div>

        {selectedArea && (
          <div className="modal-field">
            <label>Device</label>
            <select
              className="modal-select"
              value={form.deviceName}
              onChange={e => handleDeviceChange(e.target.value)}
            >
              <option value="">— Select device —</option>
              {schedulableDevs.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        )}

        {form.deviceName && (
          <div className="modal-field">
            <label>Action</label>
            <select
              className="modal-select"
              value={form.action}
              onChange={e => setField("action", e.target.value)}
            >
              <option value="">— Select action —</option>
              {actions.map(a => (
                <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
              ))}
            </select>
          </div>
        )}

        <div className="modal-field">
          <label>Time (IST)</label>
          <input
            className="modal-input"
            type="time"
            value={form.time}
            onChange={e => setField("time", e.target.value)}
          />
        </div>

        <div className="modal-field">
          <label>Repeat on Days</label>
          <div className="day-selector">
            <span
              className={`day-chip every-day${isEveryDay ? " active" : ""}`}
              onClick={toggleAllDays}
            >
              Every Day
            </span>
            {ALL_DAYS.map(day => (
              <span
                key={day}
                className={`day-chip${form.days.includes(day) ? " active" : ""}`}
                onClick={() => toggleDay(day)}
              >
                {DAY_LABELS[day]}
              </span>
            ))}
          </div>
        </div>

        <label className="modal-checkbox-row">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={e => setField("enabled", e.target.checked)}
          />
          Enable this schedule
        </label>

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
